import { useCallback, useEffect, useRef, useState } from 'react';
import type { AssistRequestDto } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * Wave 3 #13 (staff half) — "Summon a human", surfaced on the Devices board.
 *
 * The poll asks for the venue's recent requests UNFILTERED, every 10s beside
 * the device poll (the whole delivery mechanism — no staff websocket exists,
 * by design), and keeps the OPEN and ACKED rows client-side. It used to ask
 * for `status=OPEN` only and locally patch acked rows over the answer — which
 * meant a row acked HERE but resolved on another terminal was in nobody's
 * answer and never cleared (R4#9): the banner showed a raised hand forever.
 * Polling every status makes the server the one source of truth; a resolved
 * row simply stops being OPEN/ACKED and drops.
 */

const POLL_INTERVAL_MS = 10_000;

/** Local mute for the beep. Per browser, deliberately not per account. */
const MUTE_STORAGE_KEY = 'gc.assist.muted';

/**
 * Which ids in `next` were not in `prev` — the beep trigger. Pure, so the
 * "one beep per raised hand, none on every poll" rule is testable in node.
 */
export function newRequestIds(
  prev: ReadonlySet<string>,
  next: readonly AssistRequestDto[],
): string[] {
  return next.filter((request) => !prev.has(request.id)).map((request) => request.id);
}

/**
 * The banner's rows from one (unfiltered) poll answer: OPEN and ACKED only,
 * oldest first. RESOLVED rows — whoever resolved them, on whichever terminal
 * — drop here, which is exactly the R4#9 fix: the poll is the whole truth,
 * and nothing locally remembered can outlive it.
 */
export function activeAssistRequests(polled: readonly AssistRequestDto[]): AssistRequestDto[] {
  return (
    polled
      .filter((request) => request.status === 'OPEN' || request.status === 'ACKED')
      // Oldest first — the customer who has waited longest tops the banner.
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
  );
}

function readStoredMute(): boolean {
  try {
    return window.localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Two short rising tones through the Web Audio API. Failure is swallowed —
 * a beep the browser refuses (no gesture yet, no audio device) must never
 * break the board that is showing the visual alert anyway.
 */
export function playAssistBeep(): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    const context = new Ctor();
    for (const [frequency, at] of [
      [660, 0],
      [880, 0.18],
    ] as const) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, context.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.2, context.currentTime + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + at + 0.15);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(context.currentTime + at);
      oscillator.stop(context.currentTime + at + 0.16);
    }
    window.setTimeout(() => void context.close().catch(() => {}), 600);
  } catch {
    // Silence is an acceptable failure mode; a crashed board is not.
  }
}

export interface AssistApi {
  /** The venue's OPEN and ACKED requests, oldest first — the server's truth. */
  requests: AssistRequestDto[];
  muted: boolean;
  toggleMuted: () => void;
  ack: (id: string) => Promise<void>;
  resolve: (id: string) => Promise<void>;
  /** Last ack/resolve failure, for the banner to surface. */
  actionError: string | null;
}

export function useAssist(): AssistApi {
  const [requests, setRequests] = useState<AssistRequestDto[]>([]);
  const [muted, setMuted] = useState(readStoredMute);
  const [actionError, setActionError] = useState<string | null>(null);

  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const seenIdsRef = useRef<ReadonlySet<string>>(new Set());

  const load = useCallback(async () => {
    let polled: AssistRequestDto[];
    try {
      // Unfiltered on purpose (the API's list is capped at a floor's worth):
      // asking for OPEN only is how resolved-elsewhere ACKED rows got stuck.
      polled = await api.get<AssistRequestDto[]>('/assist');
    } catch {
      // A failed poll keeps the last known truth; the next tick retries.
      return;
    }

    // Beep for rows that are still WAITING and never seen before. A request
    // first sighted as ACKED/RESOLVED was handled from another terminal —
    // under the old OPEN-only poll it never beeped, and must not start now.
    const waiting = polled.filter((request) => request.status === 'OPEN');
    if (newRequestIds(seenIdsRef.current, waiting).length > 0 && !mutedRef.current) {
      playAssistBeep();
    }
    // Every polled id counts as seen — including ones arriving while muted;
    // unmuting must not replay old news.
    seenIdsRef.current = new Set([...seenIdsRef.current, ...polled.map((request) => request.id)]);

    setRequests(activeAssistRequests(polled));
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const toggleMuted = useCallback(() => {
    setMuted((current) => {
      const next = !current;
      try {
        window.localStorage.setItem(MUTE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Storage refused (private mode); the toggle still works this session.
      }
      return next;
    });
  }, []);

  const act = useCallback(async (id: string, action: 'ack' | 'resolve') => {
    setActionError(null);
    try {
      const updated = await api.post<AssistRequestDto>(`/assist/${id}/${action}`);
      setRequests((known) =>
        action === 'resolve' || updated.status === 'RESOLVED'
          ? known.filter((request) => request.id !== id)
          : known.map((request) => (request.id === id ? updated : request)),
      );
    } catch (caught) {
      setActionError(caught instanceof ApiError ? caught.message : 'Request failed.');
    }
  }, []);

  const ack = useCallback((id: string) => act(id, 'ack'), [act]);
  const resolve = useCallback((id: string) => act(id, 'resolve'), [act]);

  return { requests, muted, toggleMuted, ack, resolve, actionError };
}
